// Aperçu en deux onglets (05/10, accord de Louka), sur le modèle du DocumentViewerSheet de l'ancienne
// app : « Original » (le fichier rendu exactement comme avant, onglet par défaut) et « Texte extrait »
// (le texte extrait et indexé). Sans source de texte, l'original s'affiche seul, sans barre d'onglets.
// Monté autour du corps d'aperçu du panneau de conversation (FilePreviewPanel) et du tiroir des
// dossiers (TiroirApercuFichier).
import type { EtatTexteExtrait } from "@app/components/compta/apercu/useTexteExtrait";
import { useTexteExtrait } from "@app/components/compta/apercu/useTexteExtrait";
import { t } from "@app/lib/compta/i18n";
import type { SourceTexteExtrait } from "@app/lib/compta/texte_extrait";
import type { LightWorkspaceType } from "@app/types/user";
import {
  ButtonsSwitch,
  ButtonsSwitchList,
  cn,
  Markdown,
  Spinner,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";
import { useState } from "react";

type Onglet = "original" | "texte";

interface OngletsApercuProps {
  owner: LightWorkspaceType;
  /** D'où lire le texte extrait ; null = pas d'onglet « Texte extrait ». */
  source: SourceTexteExtrait | null;
  /** Le corps d'aperçu existant (onglet « Original »). */
  children: ReactNode;
}

export function OngletsApercu({ owner, source, children }: OngletsApercuProps) {
  const [onglet, setOnglet] = useState<Onglet>("original");

  if (!source) {
    return <>{children}</>;
  }

  const surOriginal = onglet === "original";
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 justify-center border-b border-border bg-muted-background px-4 py-2 dark:border-border-night dark:bg-muted-background-night">
        <ButtonsSwitchList
          value={onglet}
          size="xs"
          onValueChange={(valeur) => {
            if (valeur === "original" || valeur === "texte") {
              setOnglet(valeur);
            }
          }}
        >
          <ButtonsSwitch value="original" label={t("Original")} />
          <ButtonsSwitch value="texte" label={t("Extracted text")} />
        </ButtonsSwitchList>
      </div>
      {/* L'original reste monté (le PDF ne se recharge pas au retour sur l'onglet). */}
      <div
        hidden={!surOriginal}
        className={cn(
          "min-h-0 flex-1 flex-col",
          surOriginal ? "flex" : "hidden"
        )}
      >
        {children}
      </div>
      {!surOriginal && (
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted-background p-4 dark:bg-muted-background-night">
          <TexteExtrait owner={owner} source={source} />
        </div>
      )}
    </div>
  );
}

function TexteExtrait({
  owner,
  source,
}: {
  owner: LightWorkspaceType;
  source: SourceTexteExtrait;
}) {
  const etat = useTexteExtrait({ owner, source });
  return <VueTexteExtrait etat={etat} />;
}

export function VueTexteExtrait({ etat }: { etat: EtatTexteExtrait }) {
  switch (etat.etat) {
    case "chargement":
      return (
        <div className="flex flex-col items-center gap-3 py-8">
          <Spinner size="sm" />
          <p className="text-sm text-muted-foreground dark:text-muted-foreground-night">
            {t("Loading the extracted text…")}
          </p>
        </div>
      );
    case "erreur":
      return (
        <p className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground-night">
          {t("The extracted text could not be loaded.")}
        </p>
      );
    case "pret":
      if (etat.texte.trim() === "") {
        return (
          <p className="py-8 text-center text-sm text-muted-foreground dark:text-muted-foreground-night">
            {t("No text was extracted from this document.")}
          </p>
        );
      }
      return (
        <div className="mx-auto w-full max-w-3xl rounded-xl bg-background p-6 text-foreground dark:bg-background-night dark:text-foreground-night">
          {etat.markdown ? (
            <Markdown content={etat.texte} />
          ) : (
            <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
              {etat.texte}
            </pre>
          )}
        </div>
      );
  }
}
